import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdSearch, MdCheckCircle, MdCancel, MdInbox, MdVisibility } from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import ImageCropper from '../components/ImageCropper';
import DragDropImageUpload from '../components/DragDropImageUpload';
import { formatDateTime, formatINR, statusBadgeClass } from '../utils/format';

const TABS = ['pending', 'accepted', 'rejected', 'all'];

export default function ContributorReview() {
  const [tab, setTab] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reasons, setReasons] = useState([]);

  const [detail, setDetail] = useState(null);
  const [acceptFor, setAcceptFor] = useState(null);
  const [amount, setAmount] = useState('');
  const [rejectFor, setRejectFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);

  const handlePreAccept = async (submission) => {
    // Duplicate-check feature disabled for now — commented out, not removed.
    // setCheckingDuplicate(true);
    // try {
    //   const res = await api.post('/admin/articles/check-duplicate', {
    //     title: submission.title,
    //     summary: submission.matter,
    //     category: submission.category
    //   });
    //   if (res.data.success && res.data.has_duplicates) {
    //     setDuplicateWarning({ submission, duplicates: res.data.duplicates });
    //   } else {
    //     setAcceptFor(submission);
    //     setAmount('');
    //   }
    // } catch (err) {
    //   setAcceptFor(submission);
    //   setAmount('');
    // } finally {
    //   setCheckingDuplicate(false);
    // }
    setAcceptFor(submission);
    setAmount('');
  };

  // Edit-before-accept
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });
  const [editUploading, setEditUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]); // [{name, state}]
  const states = [...new Set(cities.map((c) => c.state).filter(Boolean))];

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (search) params.search = search;
      const res = await api.get('/admin/contributor-submissions', { params });
      setList(res.data.data || []);
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    api.get('/admin/contributors/meta/rejection-reasons')
      .then((res) => setReasons(res.data.data?.submission || []))
      .catch(() => { });
    // Dropdown sources for the edit form (same as PublishArticle).
    api.get('/categories').then((r) => setCategories((r.data.data || []).map((c) => c.name))).catch(() => { });
    api.get('/cities').then((r) => setCities((r.data.data || []).map((c) => ({ name: c.name, state: c.state })))).catch(() => { });
  }, []);

  const startEdit = (s) => {
    setForm({
      title: s.title || '',
      matter: s.matter || '',
      category: s.category || '',
      state: s.state || '',
      city: s.city || '',
      is_breaking: !!s.is_breaking,
      media_type: s.media_type || 'image',
      image_url: s.image_url || '',
      video_url: s.video_url || '',
      thumbnail_url: s.thumbnail_url || '',
    });
    setEditing(true);
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = type === 'image' ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      if (type === 'image') setForm(f => ({ ...f, image_url: url }));
      else if (type === 'thumbnail') setForm(f => ({ ...f, thumbnail_url: url }));
      else setForm(f => ({ ...f, video_url: url }));
      toast.success(`${type} uploaded!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/contributor-submissions/${detail.id}`, form);
      setDetail({ ...detail, ...res.data.data });
      setEditing(false);
      toast.success('Submission updated');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally { setBusy(false); }
  };

  const closeDetail = () => { setDetail(null); setEditing(false); };

  const accept = async () => {
    const amt = Number(amount);
    if (!(amt >= 0)) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await api.patch(`/admin/contributor-submissions/${acceptFor.id}/accept`, { earning_amount: amt });
      toast.success('Accepted & published');
      setAcceptFor(null); setAmount(''); setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const reject = async (reason) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributor-submissions/${rejectFor.id}/reject`, { reason });
      toast.success('Rejected');
      setRejectFor(null); setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Contributor  Review News</h2>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input className="search-input" placeholder="Search title…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <MdInbox className="empty-icon" />
          <h3>Queue empty</h3>
          <p>No “{tab}” submissions.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Title</th><th>Contributor</th><th>Category</th><th>Location</th><th>Submitted</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id}>
                  <td>{s.title || <em>Untitled</em>}</td>
                  <td>{s.contributor?.name || '-'}</td>
                  <td>{s.category || '-'}</td>
                  <td>{[s.city, s.state].filter(Boolean).join(', ') || '-'}</td>
                  <td>{formatDateTime(s.submitted_at)}</td>
                  <td><span className={`badge ${statusBadgeClass(s.status)}`}>{s.status}</span></td>
                  <td><button className="btn btn-sm btn-ghost" onClick={() => setDetail(s)}>Review</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review modal — preview or edit */}
      {detail && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Submission' : 'Review Submission'}</h3>
              <button className="modal-close" onClick={closeDetail}><MdClose /></button>
            </div>
            <div className="modal-body">
              {!editing && (
                detail.media_type === 'video' && detail.video_url ? (
                  <video src={detail.video_url} controls style={{ width: '100%', borderRadius: 12, marginBottom: 12, maxHeight: 280 }} />
                ) : detail.image_url ? (
                  <div style={{ aspectRatio: '720/590', borderRadius: 12, marginBottom: 12, overflow: 'hidden', background: '#f1f5f9' }}>
                    <img src={detail.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                ) : null
              )}

              {editing ? (
                <>
                  <div className="form-group">
                    <label>Media Type</label>
                    <div className="radio-group">
                      <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                        <input type="radio" value="image" checked={form.media_type === 'image'}
                          onChange={() => setForm(f => ({ ...f, media_type: 'image' }))} /> Image
                      </label>
                      <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                        <input type="radio" value="video" checked={form.media_type === 'video'}
                          onChange={() => setForm(f => ({ ...f, media_type: 'video' }))} /> Video
                      </label>
                    </div>
                  </div>

                  {form.media_type === 'image' && (
                    <div className="form-group">
                      <label>Article Image (Web & App)</label>
                      <DragDropImageUpload
                        type="image"
                        uploading={editUploading}
                        imageUrl={form.image_url}
                        onFileSelect={handleEditFileSelect}
                      />
                      <input type="text" value={form.image_url}
                        onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="Or paste image URL" className="mt-sm" />
                    </div>
                  )}

                  {form.media_type === 'video' && (
                    <>
                      <div className="form-group">
                        <label>Video URL</label>
                        <input type="text" value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="MP4 URL..." />
                        <button type="button" className="btn btn-xs mt-xs" onClick={() => handleEditFileSelect('video')}>Upload Video</button>
                      </div>
                      <div className="form-group">
                        <label>Video Thumbnail</label>
                        <DragDropImageUpload
                          type="thumbnail"
                          uploading={editUploading}
                          imageUrl={form.thumbnail_url}
                          onFileSelect={handleEditFileSelect}
                        />
                        <input type="text" value={form.thumbnail_url} onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))} placeholder="Or paste thumbnail URL" className="mt-sm" />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <div className="flex-row justify-between items-center mb-1">
                      <label className="mb-0">Title <span className="char-count">{(form.title || '').length}/75</span></label>
                    </div>
                    <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required />
                  </div>
                  <div className="form-group">
                    <div className="flex-row justify-between items-center mb-1">
                      <label className="mb-0">Matter <span className="char-count">{(form.matter || '').length}/400</span></label>
                    </div>
                    <textarea rows={6} value={form.matter} onChange={(e) => setForm((f) => ({ ...f, matter: e.target.value.slice(0, 400) }))} maxLength={400} required />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                      <option value="">Select</option>
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minHeight: '42px' }}>
                      {['Andhra Pradesh', 'Telangana', 'All India'].map(stateName => {
                        let isSelected = false;
                        if (form.state) {
                          const sTrim = form.state.trim();
                          if (sTrim === stateName || (stateName === 'Andhra Pradesh' && (sTrim === 'ఆంధ్రప్రదేశ్' || sTrim === 'ఆంధ్ర ప్రదేశ్')) || (stateName === 'Telangana' && sTrim === 'తెలంగాణ') || (stateName === 'All India' && (sTrim === 'అల్ ఇండియా' || sTrim === 'అల్ ఇండియా '))) {
                            isSelected = true;
                          }
                        }
                        return (
                          <label key={stateName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: isSelected ? '500' : '400' }}>
                            <input
                              type="radio"
                              name="edit_quick_state"
                              checked={isSelected}
                              onChange={() => setForm(f => ({ ...f, state: stateName, city: '' }))}
                              style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                            />
                            {stateName}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <select value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}>
                      <option value="">Select</option>
                      {cities.filter((c) => !form.state || c.state === form.state).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                    <input type="checkbox" checked={!!form.is_breaking} onChange={(e) => setForm((f) => ({ ...f, is_breaking: e.target.checked }))} />
                    Breaking news
                  </label>
                </>
              ) : (
                <>
                  <h3 style={{ marginBottom: 8 }}>{detail.title}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    {detail.category && <span className="badge badge-category">{detail.category}</span>}
                    {detail.is_breaking && <span className="badge badge-danger">Breaking</span>}
                    <span className="badge badge-neutral">{[detail.city, detail.state].filter(Boolean).join(', ')}</span>
                  </div>
                  <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--text)' }}>{detail.matter}</p>

                  <div className="detail-row" style={{ marginTop: 16 }}>
                    <span className="detail-label">Contributor</span>
                    <span>{detail.contributor?.name} ({detail.contributor?.email})</span>
                  </div>
                  {detail.status === 'accepted' && (
                    <div className="detail-row"><span className="detail-label">Earning credited</span><span>{formatINR(detail.earning_amount)}</span></div>
                  )}
                  {detail.status === 'rejected' && detail.rejection_reason && (
                    <div className="detail-row"><span className="detail-label">Rejection reason</span><span>{detail.rejection_reason}</span></div>
                  )}
                </>
              )}
            </div>

            {detail.status === 'pending' && (
              <div className="modal-footer">
                {editing ? (
                  <>
                    <button className="btn btn-ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel Edit</button>
                    <button className="btn btn-primary" disabled={busy} onClick={saveEdit}>{busy ? 'Saving…' : 'Save Changes'}</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-ghost" onClick={() => startEdit(detail)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => setRejectFor(detail)}><MdCancel /> Reject</button>
                    <button className="btn btn-primary" disabled={checkingDuplicate} onClick={() => handlePreAccept(detail)}>
                      {checkingDuplicate ? 'Checking duplicates...' : <><MdCheckCircle /> Accept</>}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Accept → earning amount */}
      {acceptFor && (
        <div className="modal-overlay" onClick={() => setAcceptFor(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Accept & Credit Contributor</h3>
              <button className="modal-close" onClick={() => setAcceptFor(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="text-muted mb-sm">"{acceptFor.title}"</p>
              <div className="form-group">
                <label>Earning Amount (₹)</label>
                <input type="number" min="0" value={amount} placeholder="e.g. 10" onChange={(e) => setAmount(e.target.value)} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAcceptFor(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={accept} disabled={busy || amount === ''}>
                {busy ? 'Publishing…' : 'Accept & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title="Reject Submission"
          reasons={reasons}
          confirmLabel="Reject Submission"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={reject}
        />
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This contributor submission is very similar to an existing published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before accepting.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {dup.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.ai_reason ? `${Math.round(dup.ai_confidence * 100)}%` : `${dup.score}%`}
                    </span>
                  </div>

                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  {dup.ai_reason && (
                    <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                      {dup.ai_reason}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#475569', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Matched Article
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="btn btn-ghost" onClick={() => setDuplicateWarning(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const sub = duplicateWarning.submission;
                  setDuplicateWarning(null);
                  setAcceptFor(sub);
                  setAmount('');
                }}
              >
                Proceed & Accept Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Matched Article Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, borderRadius: 16 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Published Article Preview</h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>
            <div className="modal-body" style={{ padding: 24 }}>
              {viewMatchedArticle.image_url && (
                <img src={viewMatchedArticle.image_url} alt="" style={{ width: '100%', borderRadius: 12, marginBottom: 16, maxHeight: 250, objectFit: 'cover' }} />
              )}
              <h4 style={{ marginTop: 0, marginBottom: 8, fontSize: 18 }}>{viewMatchedArticle.title}</h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <span className="badge badge-category">{viewMatchedArticle.category}</span>
                <span className="badge badge-neutral">{new Date(viewMatchedArticle.published_at).toLocaleDateString()}</span>
              </div>
              <p style={{ color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{viewMatchedArticle.summary}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          aspectRatio={editCropper.type === 'thumbnail' ? 300 / 169 : 720 / 590}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}
    </div>
  );
}
